import React from "react";
import PropTypes from "prop-types";
import { Box, Divider, Typography } from "@mui/material";
import { SIDEBAR } from "./sidebarVisual";

/** Shared scrollable panel under the V2 tab rail — fills remaining drawer height. */
export function SidebarPanel({ children, testId, compact = false }) {
  return (
    <Box
      data-testid={testId}
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        pt: compact ? SIDEBAR.gap.withinCard : SIDEBAR.contentPadTop,
        pb: compact ? SIDEBAR.gap.card : SIDEBAR.contentPadBottom,
        px: SIDEBAR.contentPadX,
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        "& .MuiCard-root": {
          width: "100% !important",
          maxWidth: "100%",
        },
      }}
    >
      {children}
    </Box>
  );
}

SidebarPanel.propTypes = {
  children: PropTypes.node,
  testId: PropTypes.string,
  compact: PropTypes.bool,
};

/** Primary section title used across all V2 tabs. */
export function SidebarSectionTitle({ children, sx: sxProp }) {
  return (
    <Typography
      component="h3"
      sx={{
        m: 0,
        mb: SIDEBAR.gap.sectionTitleToContent,
        ...SIDEBAR.type.sectionTitle,
        ...sxProp,
      }}
    >
      {children}
    </Typography>
  );
}

SidebarSectionTitle.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};

/** Secondary label (Emails, Phone, Keywords, …). */
export function SidebarLabel({ children, sx: sxProp }) {
  return (
    <Typography
      component="div"
      sx={{
        m: 0,
        mb: SIDEBAR.gap.labelToValue,
        ...SIDEBAR.type.label,
        ...sxProp,
      }}
    >
      {children}
    </Typography>
  );
}

SidebarLabel.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};

/** Body / description copy. */
export function SidebarBody({ children, secondary = false, sx: sxProp }) {
  return (
    <Typography
      component="div"
      sx={{
        m: 0,
        ...(secondary ? SIDEBAR.type.bodySecondary : SIDEBAR.type.body),
        ...sxProp,
      }}
    >
      {children}
    </Typography>
  );
}

SidebarBody.propTypes = {
  children: PropTypes.node,
  secondary: PropTypes.bool,
  sx: PropTypes.object,
};

/**
 * Section block with optional soft divider underneath.
 */
export function SidebarSection({ title, children, showDivider = true }) {
  return (
    <Box>
      {title ? <SidebarSectionTitle>{title}</SidebarSectionTitle> : null}
      {children}
      {showDivider ? (
        <Divider
          sx={{
            mt: SIDEBAR.gap.section,
            mb: SIDEBAR.gap.section,
            borderColor: "rgba(88, 89, 91, 0.10)",
          }}
        />
      ) : null}
    </Box>
  );
}

SidebarSection.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  showDivider: PropTypes.bool,
};

/** Soft read-only information surface (not an input). */
export function SidebarCard({ children, sx: sxProp, testId }) {
  return (
    <Box
      data-testid={testId}
      sx={{
        border: SIDEBAR.border,
        borderRadius: SIDEBAR.radius,
        bgcolor: SIDEBAR.surface,
        boxShadow: SIDEBAR.shadow,
        px: SIDEBAR.cardPadX,
        py: SIDEBAR.cardPadY,
        ...sxProp,
      }}
    >
      {children}
    </Box>
  );
}

SidebarCard.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
  testId: PropTypes.string,
};

/** Soft light-gray surface used for all Details data points. */
export function MutedBlock({ children, sx: sxProp, testId }) {
  return (
    <Box
      data-testid={testId}
      sx={{
        bgcolor: SIDEBAR.surfaceReadonly,
        borderRadius: SIDEBAR.radiusSm,
        border: SIDEBAR.borderSoft,
        px: SIDEBAR.cardPadX,
        py: SIDEBAR.cardPadY,
        boxShadow: "none",
        ...sxProp,
      }}
    >
      {children}
    </Box>
  );
}

MutedBlock.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
  testId: PropTypes.string,
};

/**
 * Read-only informational block — light gray wash, no input chrome.
 * Used for descriptions and other immutable text.
 */
export function ReadOnlyBlock({ children, testId = "read-only-block", sx: sxProp }) {
  return (
    <MutedBlock testId={testId} sx={sxProp}>
      <Typography
        component="div"
        sx={{
          m: 0,
          ...SIDEBAR.type.bodySecondary,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {children}
      </Typography>
    </MutedBlock>
  );
}

ReadOnlyBlock.propTypes = {
  children: PropTypes.node,
  testId: PropTypes.string,
  sx: PropTypes.object,
};

/** Compact label → value row; value sits in a muted gray block. */
export function SidebarField({ label, children }) {
  return (
    <Box sx={{ mb: SIDEBAR.gap.field, "&:last-child": { mb: 0 } }}>
      <SidebarLabel>{label}</SidebarLabel>
      <MutedBlock>
        <Box sx={{ ...SIDEBAR.type.body }}>{children}</Box>
      </MutedBlock>
    </Box>
  );
}

SidebarField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

/** Vertical stack with standardized card/list gap. */
export function SidebarStack({ children, gap = SIDEBAR.gap.card, sx: sxProp }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap,
        ...sxProp,
      }}
    >
      {children}
    </Box>
  );
}

SidebarStack.propTypes = {
  children: PropTypes.node,
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  sx: PropTypes.object,
};
